import {
  CheckboxProps,
  FormControl,
  FormControlLabel,
  Checkbox as MuiCheckbox,
  SxProps,
  Theme,
  styled,
  useTheme,
} from '@mui/material';
import { Square, SquareCheckBig, SquareMinus } from 'lucide-react';
import { Controller, UseControllerProps } from 'react-hook-form';

type CheckboxSelectItemProps = CheckboxProps & {
  checked?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  dark?: boolean;
  hiddenLabel?: boolean;
  label?: string;
  controller?: UseControllerProps<any>;
  sx?: SxProps<Theme> | undefined;
  iconSize?: number;
};

type StyleProps = {
  dark?: boolean;
};

const StyledCheckbox = styled(MuiCheckbox, {
  shouldForwardProp: (props) => props !== 'dark',
})<StyleProps>(({ theme, dark }) => ({
  color: dark ? theme.palette.darkBlue.main : theme.palette.common.white,
}));

const Checkbox: React.FC<CheckboxSelectItemProps> = ({
  checked,
  onChange,
  icon,
  checkedIcon,
  controller,
  label,
  iconSize = 18,
  dark = false,
  hiddenLabel = false,
  sx,
  ...props
}) => {
  const theme = useTheme();

  const color = dark ? theme.palette.darkBlue.main : theme.palette.common.white;

  return (
    <FormControl variant="standard">
      {controller ? (
        <Controller
          {...controller}
          render={({ field, fieldState }) => {
            return (
              <FormControlLabel
                control={
                  <StyledCheckbox
                    {...props}
                    sx={sx}
                    dark={dark}
                    disableRipple
                    checked={field.value || false}
                    icon={icon || <Square size={iconSize} color={color} />}
                    checkedIcon={checkedIcon || <SquareCheckBig size={iconSize} color={color} />}
                    indeterminateIcon={<SquareMinus size={iconSize} color={color} />}
                    onChange={(event, checked) => {
                      field.onChange(checked);
                      !!onChange && onChange(event, checked);
                    }}
                  />
                }
                sx={{
                  userSelect: 'none',
                  '& .MuiTypography-root': {
                    color: dark ? theme.palette.darkBlue.main : theme.palette.common.white,
                  },
                }}
                hidden
                label={label}
              />
            );
          }}
        />
      ) : (
        <FormControlLabel
          control={
            <StyledCheckbox
              {...props}
              sx={sx}
              dark={dark}
              disableRipple
              checked={checked}
              icon={icon || <Square size={iconSize} color={color} />}
              checkedIcon={checkedIcon || <SquareCheckBig size={iconSize} color={color} />}
              indeterminateIcon={<SquareMinus size={iconSize} color={color} />}
              onChange={onChange}
            />
          }
          sx={{
            userSelect: 'none',
            '& .MuiTypography-root': {
              color: dark ? theme.palette.darkBlue.main : theme.palette.common.white,
            },
          }}
          hidden
          label={label}
        />
      )}
    </FormControl>
  );
};

export default Checkbox;
